/**
 * Credor que possui teto especial para cobrança.
 * @type {string}
 */
const CREDOR_ID_MAX_25000 = '6e39ee2c-55e6-4e3b-bab5-623e377dd56b';

/**
 * Valor mínimo padrão permitido para cobrança.
 * @type {number}
 */
const VALOR_MINIMO_COBRANCA = 30;

/**
 * Valor máximo padrão permitido para cobrança.
 * @type {number}
 */
const VALOR_MAXIMO_PADRAO_COBRANCA = 10000;

/**
 * Valor máximo excepcional permitido para cobrança.
 * @type {number}
 */
const VALOR_MAXIMO_EXCEPCIONAL_COBRANCA = 25000;

/**
 * Converte um valor monetário/numérico em número.
 * Aceita number, string simples e string no padrão BR.
 *
 * @param {number|string|null|undefined} value
 * @returns {number}
 */
function parseNumericValue(value) {
    if (value === null || value === undefined || value === '') {
        return NaN;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : NaN;
    }

    const normalizedValue = String(value)
        .trim()
        .replace(/\s/g, '')
        .replace(/^R\$\s?/, '')
        .replace(/\./g, '')
        .replace(',', '.');

    const parsedValue = Number(normalizedValue);

    return Number.isFinite(parsedValue) ? parsedValue : NaN;
}

/**
 * Formata valor numérico para exibição em pt-BR sem casas decimais,
 * seguindo o padrão visual já usado na aplicação.
 *
 * @param {number} value
 * @returns {string}
 */
function formatCurrencyInteger(value) {
    return value.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
}

/**
 * Retorna o valor máximo permitido para o credor informado.
 *
 * @param {string|null|undefined} credorId
 * @returns {number}
 */
export function getMaxValorRecebivelByCredor(credorId) {
    if (credorId === CREDOR_ID_MAX_25000) {
        return VALOR_MAXIMO_EXCEPCIONAL_COBRANCA;
    }

    return VALOR_MAXIMO_PADRAO_COBRANCA;
}

/**
 * Retorna a mensagem padrão de faixa de valor permitida para o credor.
 *
 * @param {string|null|undefined} credorId
 * @returns {string}
 */
function getMensagemFaixaValorRecebivel(credorId) {
    const valorMaximo = getMaxValorRecebivelByCredor(credorId);

    return `Por favor "Valor do Boleto" deve estar entre R$ ${formatCurrencyInteger(VALOR_MINIMO_COBRANCA)} e R$ ${formatCurrencyInteger(valorMaximo)}.`;
}

/**
 * Verifica se um valor e numerico e maior que zero.
 *
 * @param {number|string|null|undefined} value
 * @returns {boolean}
 */
export function isNumeroPositivoValido(value) {
    const parsedValue = parseNumericValue(value);
    return Number.isFinite(parsedValue) && parsedValue > 0;
}

/**
 * Verifica se a quantidade de parcelas usada no simulador e valida.
 *
 * @param {number|string|null|undefined} parcelaValue
 * @returns {boolean}
 */
export function isSimulaParcelaValida(parcelaValue) {
    return isNumeroPositivoValido(parcelaValue);
}

/**
 * Valida se o valor do recebível está dentro da faixa permitida para o credor.
 *
 * @param {number|string|null|undefined} value
 * @param {string|null|undefined} credorId
 * @returns {boolean}
 */
function isValorRecebivelValido(value, credorId) {
    const parsedValue = parseNumericValue(value);

    if (!Number.isFinite(parsedValue)) {
        return false;
    }

    const valorMaximo = getMaxValorRecebivelByCredor(credorId);

    return parsedValue >= VALOR_MINIMO_COBRANCA && parsedValue <= valorMaximo;
}

/**
 * Valida os campos do submit de cadastro de recebivel.
 *
 * @param {object} formData
 * @param {string|number} formData.recebivelTipoId
 * @param {number|string} formData.recebivelTipoValor
 * @param {Date|string|null} formData.recebivelTipoDataInicio
 * @param {number|string|null} formData.recebivelTipoVencimento
 * @param {string|number|null} formData.recebivelTipoDesconto
 * @param {string|null} formData.recebivelTipoDescontoTipo
 * @param {string|null|undefined} [formData.credorId]
 * @returns {string|null}
 */
export function validateRecebivelSubmitFields(formData) {
    const {
        recebivelTipoId,
        recebivelTipoValor,
        recebivelTipoDataInicio,
        recebivelTipoVencimento,
        recebivelTipoDesconto,
        recebivelTipoDescontoTipo,
        credorId,
    } = formData;

    const valorRecebivel = parseNumericValue(recebivelTipoValor);
    const valorDesconto = parseNumericValue(recebivelTipoDesconto);

    if (recebivelTipoId === '') {
        return 'Escolha uma Descrição para sua cobrança.';
    }

    if (!isValorRecebivelValido(recebivelTipoValor, credorId)) {
        return getMensagemFaixaValorRecebivel(credorId);
    }

    if (recebivelTipoDataInicio === '') {
        return 'Por favor coloque uma Data de início.';
    }

    if (recebivelTipoVencimento === null || recebivelTipoVencimento < 1) {
        return 'Por Favor coloque um dia de Vencimento válido';
    }

    if (recebivelTipoDescontoTipo !== '0' && recebivelTipoDescontoTipo !== '') {
        if (recebivelTipoDesconto === '') {
            return 'É necessário definir um valor para desconto ou retirar o tipo de desconto.';
        }
    } else if (recebivelTipoDesconto !== '') {
        if (recebivelTipoDescontoTipo === '0' || recebivelTipoDescontoTipo === '') {
            return 'É necessário escolher um tipo de desconto ou apagar o valor do desconto.';
        }
    }

    if (recebivelTipoDescontoTipo === '%') {
        const valorCalculadoDesconto = valorRecebivel * (valorDesconto / 100);
        const valorDescontado = valorRecebivel - valorCalculadoDesconto;

        if (valorDescontado < 10) {
            return 'Valor com o desconto dado e inferior a R$ 10. Favor Verificar, o valor ou o desconto.';
        }
    }

    if (recebivelTipoDescontoTipo === 'R$') {
        const valorDescontado = valorRecebivel - valorDesconto;

        if (valorDescontado < 10) {
            return 'Valor com o desconto dado e inferior a R$ 10. Favor Verificar, o valor ou o desconto.';
        }
    }

    return null;
}

/**
 * Valida os campos do submit de atualizacao de recebivel.
 *
 * @param {object} formData
 * @param {number|string} formData.recebivelTipoValor
 * @param {Date|string|null} formData.recebivelTipoDataInicio
 * @param {number|string|null} formData.recebivelTipoVencimento
 * @param {string|number|null} formData.recebivelTipoDesconto
 * @param {string|null} formData.recebivelTipoDescontoTipo
 * @param {string|null|undefined} [formData.credorId]
 * @returns {string|null}
 */
export function validateRecebivelUpdateFields(formData) {
    const {
        recebivelTipoValor,
        recebivelTipoDataInicio,
        recebivelTipoVencimento,
        recebivelTipoDesconto,
        recebivelTipoDescontoTipo,
        credorId,
    } = formData;

    const valorRecebivel = parseNumericValue(recebivelTipoValor);
    const valorDesconto = parseNumericValue(recebivelTipoDesconto);

    if (!isValorRecebivelValido(recebivelTipoValor, credorId)) {
        return getMensagemFaixaValorRecebivel(credorId);
    }

    if (recebivelTipoDataInicio === '' || recebivelTipoDataInicio === null) {
        return 'Escolha uma data de início';
    }

    if ((recebivelTipoDescontoTipo !== '0' && recebivelTipoDescontoTipo !== '') && (recebivelTipoDesconto === '' || recebivelTipoDesconto === null)) {
        return 'É necessário definir um valor para desconto ou retirar o tipo de desconto.';
    }

    if (recebivelTipoDesconto && (recebivelTipoDescontoTipo === '0' || recebivelTipoDescontoTipo === '' || recebivelTipoDescontoTipo === null)) {
        return 'É necessário definir um Tipo para o desconto ou retirar o tipo de desconto.';
    }

    if (recebivelTipoVencimento === null || (recebivelTipoVencimento < 1 || recebivelTipoVencimento > 31)) {
        return 'Por Favor coloque uma dia de Vencimento válido';
    }

    if (recebivelTipoDescontoTipo === '%') {
        const valorCalculadoDesconto = valorRecebivel * (valorDesconto / 100);
        const valorDescontado = valorRecebivel - valorCalculadoDesconto;

        if (valorDescontado < 10) {
            return 'Valor com o desconto dado e inferior a R$ 10. Favor Verificar, o valor ou o desconto.';
        }
    }

    if (recebivelTipoDescontoTipo === 'R$') {
        const valorDescontado = valorRecebivel - valorDesconto;

        if (valorDescontado < 10) {
            return 'Valor com o desconto dado e inferior a R$ 10. Favor Verificar, o valor ou o desconto.';
        }
    }

    return null;
}
